import { counties } from './counties.js';
import { cities } from './cities.js';
import { metros } from './metros.js';

export const locationNames = {
  Nation: ["United States"],
  Region: [
    "Northeast",
    "Midwest",
    "South",
    "West"
  ],
  Division: [
    "New England",
    "Middle Atlantic",
    "East North Central",
    "West North Central",
    "South Atlantic",
    "East South Central",
    "West South Central",
    "Mountain",
    "Pacific"
  ],
  State: [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", 
    "Delaware", "District of Columbia", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", 
    "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", 
    "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", 
    "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", 
    "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", 
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
  ],
  Metro: metros,
  County: counties,
  City: cities
};

export const stateAbbreviations = {
  'Alabama': 'AL',
  'Alaska': 'AK',
  'Arizona': 'AZ',
  'Arkansas': 'AR',
  'California': 'CA',
  'Colorado': 'CO',
  'Connecticut': 'CT',
  'Delaware': 'DE',
  'Florida': 'FL',
  'Georgia': 'GA',
  'Hawaii': 'HI',
  'Idaho': 'ID',
  'Illinois': 'IL',
  'Indiana': 'IN',
  'Iowa': 'IA',
  'Kansas': 'KS',
  'Kentucky': 'KY',
  'Louisiana': 'LA',
  'Maine': 'ME',
  'Maryland': 'MD',
  'Massachusetts': 'MA',
  'Michigan': 'MI',
  'Minnesota': 'MN',
  'Mississippi': 'MS',
  'Missouri': 'MO',
  'Montana': 'MT',
  'Nebraska': 'NE',
  'Nevada': 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  'Ohio': 'OH',
  'Oklahoma': 'OK',
  'Oregon': 'OR',
  'Pennsylvania': 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  'Tennessee': 'TN',
  'Texas': 'TX',
  'Utah': 'UT',
  'Vermont': 'VT',
  'Virginia': 'VA',
  'Washington': 'WA',
  'West Virginia': 'WV',
  'Wisconsin': 'WI',
  'Wyoming': 'WY',
  'District of Columbia': 'DC'
};

export const locationTypes = [
  { value: 'nation', label: 'Nation' },
  { value: 'region', label: 'Regions' },
  { value: 'division', label: 'Divisions' },
  { value: 'state', label: 'States' },  
  { value: 'metro', label: 'Metros' },
  { value: 'county', label: 'Counties' },
  { value: 'city', label: 'Cities' }
];

export const getStateAbbreviation = (input) => {
  if (typeof input === 'string') {
    return stateAbbreviations[input] || input;
  }
  
  if (typeof input === 'object' && input !== null) {
    const selectedLocation = input;
    
    if (!selectedLocation) return '';
    
    const isCounty = selectedLocation.info?.type?.toLowerCase() === 'county';
    if (!isCounty) return '';

    let stateName = null;

    if (selectedLocation.info?.state) {
      stateName = selectedLocation.info.state;
    }
    else if (selectedLocation.data?.current?.state) {
      stateName = selectedLocation.data.current.state;
    }
    else if (selectedLocation.info?.stateAbbr) {
      return selectedLocation.info.stateAbbr;
    }
    else if (selectedLocation.info?.name && selectedLocation.info.name.includes(',')) {
      const parts = selectedLocation.info.name.split(',');
      if (parts.length > 1) {
        stateName = parts[1].trim();
      }
    }

    return stateName ? stateAbbreviations[stateName] || stateName : '';
  }
  
  return '';
};

export const getFullStateName = (stateAbbr) => {
  const state = locationNames.State.find(
    stateName => getStateAbbreviation(stateName) === stateAbbr
  );
  return state || stateAbbr;
};

export function formatLocationNameWithStateAbbrev(locationName, locationType) {
  if (typeof locationName !== 'string' || !locationName) {
    return locationName || '';
  }
  const t = String(locationType || '').toLowerCase();
  if (t !== 'city' && t !== 'county') {
    return locationName;
  }
  const commaIdx = locationName.lastIndexOf(',');
  if (commaIdx <= 0) {
    return locationName;
  }
  const place = locationName.slice(0, commaIdx).trim();
  const statePart = locationName.slice(commaIdx + 1).trim();
  if (!statePart) {
    return locationName;
  }
  const abbr = getStateAbbreviation(statePart);
  return `${place}, ${abbr}`;
}

export const getLocationOptions = (type) => {
  const options = [];
  
  const typePlural = type === 'county' ? 'Counties' : 
                    type === 'city' ? 'Cities' :
                    type === 'metro' ? 'Metros' :
                    `${type.charAt(0).toUpperCase() + type.slice(1)}s`;

  options.push({
    value: `all_${type}`,
    label: `- All ${typePlural} -`,
    isTypeView: true,
    type: type
  });

  switch(type) {
    case 'nation':
      return locationNames.Nation.map(name => ({
        value: name.toLowerCase(),
        label: name
      }));
    case 'region':
      return [
        ...options,
        ...locationNames.Region.map(name => ({
          value: name.toLowerCase(),
          label: name
        }))
      ];
    case 'division':
      return [
        ...options,
        ...locationNames.Division.map(name => ({
          value: name.toLowerCase(),
          label: name
        }))
      ];
    case 'state':
      return [
        ...options,
        ...locationNames.State.map(name => ({
          value: name.toLowerCase(),
          label: name
        }))
      ];
    case 'metro':
      return [
        ...options,
        ...locationNames.Metro.map(name => ({
          value: name.toLowerCase().replace(/[^a-z0-9,-]/g, ''),
          label: name
        }))
      ];
    case 'county':
      return [
        ...options,
        ...locationNames.State
          .filter(state => state !== "District of Columbia")
          .map(state => ({
            value: `state_${state.toLowerCase()}`,
            label: state,
            hasSubmenu: true,
            isState: true,
            state: state
          }))
      ];
    case 'city':
      return [
        ...options,
        ...locationNames.State
          .map(state => ({
            value: `state_${state.toLowerCase()}`,
            label: state,
            hasSubmenu: true,
            isState: true,
            state: state
          }))
      ];
    default:
      return [];
  }
};

export const getCountiesForState = (state) => {
  if (!state) return [];
  
  const countiesInState = locationNames.County
    .filter(name => {
      const parts = name.split(',');
      return parts.length > 1 && parts[1].trim() === state;
    })
    .map(name => {
      const [county, state] = name.split(',').map(part => part.trim());
      return {
        value: `${county.toLowerCase()},${state.toLowerCase()}`,
        label: name,
        displayLabel: county,
        fullLabel: name,
        state: state
      };
    })
    .sort((a, b) => a.displayLabel.localeCompare(b.displayLabel));

  const stateAbbr = getStateAbbreviation(state);
  return [
    {
      value: `all_counties_${state.toLowerCase()}`,
      label: `- All ${stateAbbr} Counties -`,
      displayLabel: `All ${stateAbbr} Counties`,
      isAllStateCounties: true,
      state: state,
      stateAbbr: stateAbbr
    },
    ...countiesInState
  ];
};

export const getCitiesForState = (state) => {
  if (!state) return [];
  
  const citiesInState = locationNames.City
    .filter(name => {
      if (name.includes(',')) {
        const parts = name.split(',').map(part => part.trim());
        const stateName = parts[parts.length - 1]; 
        return stateName === state;
      }
      return false;
    })
    .map(name => {
      const parts = name.split(',').map(part => part.trim());
      const city = parts.slice(0, -1).join(', '); 
      const stateName = parts[parts.length - 1];
      return {
        value: `${city.toLowerCase()},${stateName.toLowerCase()}`,
        label: name,
        displayLabel: city,
        fullLabel: name,
        state: stateName
      };
    })
    .sort((a, b) => a.displayLabel.localeCompare(b.displayLabel));

  const stateAbbr = getStateAbbreviation(state);
  return [
    {
      value: `all_cities_${state.toLowerCase()}`,
      label: `- All ${stateAbbr} Cities -`,
      displayLabel: `All ${stateAbbr} Cities`,
      isAllStateCities: true,
      state: state,
      stateAbbr: stateAbbr
    },
    ...citiesInState
  ];
};

export const extractState = (info) => {
  if (!info) return null;
  if (info.type === 'metro') return null;
  if (info.state) return info.state.toLowerCase();
  if (info.name?.includes(',')) {
    return info.name.split(',').pop().trim().toLowerCase();
  }
  if (info.name?.startsWith('All ') && (info.type === 'county' || info.type === 'city')) {
    const statePart = info.name.replace('All ', '').replace(/ (Counties|Cities)/, '');
    return getStateAbbreviation(statePart) ? statePart.toLowerCase() : null;
  }
  return null;
};

export const isStateScopedCityCountyAggregate = (selectedLocation) => {
  const name = selectedLocation?.info?.name;
  if (!name?.startsWith('All ')) return false;
  if (name === 'All Counties' || name === 'All Cities') return false;
  return name.endsWith(' Cities') || name.endsWith(' Counties');
};

export const handleLocationSelect = (location, type, onLocationSelect, closeMenus) => {
  if (location.isTypeView) {
    const typePlural = type === 'county' ? 'Counties' : 
                      type === 'city' ? 'Cities' : 
                      type === 'metro' ? 'Metros' :
                      `${type.charAt(0).toUpperCase() + type.slice(1)}s`;
    onLocationSelect({
      type: type,
      name: `All ${typePlural}`,
      info: {
        name: `All ${typePlural}`,
        type: type,
        fromHeader: location.info?.fromHeader || false
      }
    });
  } 
  else if (location.isAllStateCounties) {
    const stateAbbr = location.stateAbbr;
    const displayName = `All ${stateAbbr} Counties`;
    
    onLocationSelect({
      type: type,
      name: displayName,
      info: {
        name: displayName,
        type: type,
        state: location.state,
        stateAbbr: stateAbbr,
        fromHeader: location.info?.fromHeader || false
      }
    });
  }
  else if (location.isAllStateCities) {
    const stateAbbr = location.stateAbbr;
    const displayName = `All ${stateAbbr} Cities`;
    
    onLocationSelect({
      type: type,
      name: displayName,
      info: {
        name: displayName,
        type: type,
        state: location.state,
        stateAbbr: stateAbbr,
        fromHeader: location.info?.fromHeader || false
      }
    });
  }
  else if ((type === 'county' || type === 'city') && location.state) {
    onLocationSelect({
      name: location.fullLabel || location.label,
      type: type,
      info: {
        name: location.fullLabel || location.label,
        type: type,
        state: location.state,
        fromHeader: location.info?.fromHeader || false
      }
    });
  }
  else {
    onLocationSelect({
      name: location.label,
      type: type,
      info: {
        name: location.label,
        type: type,
        fromHeader: location.info?.fromHeader || false
      }
    });
  }
  
  if (closeMenus) {
    closeMenus();
  }
};